// @ts-strict-ignore
import React, { useCallback, useMemo, useState } from 'react'
import { actionAtom } from 'atoms/atoms'
import { useAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import {
  ActionList,
  Avatar,
  Box,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  Icon,
  SidePanel,
  useSidePanel,
} from '@node-space/ui-components'
import { numberWithCommas } from '@node-space/utils'
import { appBannersId } from 'components/app/AppBanners'
import { SidePanelHeaderTemplateTwo } from 'components/SidePanel/SidePanelHeaderTemplates/SidePanelHeaderTemplateTwo'
import { useMerchantsQuery } from 'hooks/queries/useMerchantsQuery'
import { useComponentHeight } from 'hooks/useComponentHeight'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { ActionIds, MerchantType } from 'types/types'
import { highlightRowBasedOnId } from 'utils/table/table'
import { MerchantDetailsSummary } from './MerchantDetailsSummary'
import { MerchantSheet } from './MerchantSheet'

export const MerchantTable = () => {
  const { t } = useTranslation()
  const bannerSpacing = useComponentHeight(appBannersId)
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [action, setAction] = useAtom(actionAtom)
  const { data: { merchantList = [] } = {} } = useMerchantsQuery()

  const permissions = useMappedRoles()?.manageMerchants
  const canEdit = permissions?.canEdit

  const {
    isSidePanelOpen: isOpen,
    sidePanelData: selectedDetailsMerchant,
    paging: pageMerchants,
    setIsSidePanelOpen: setIsOpen,
    updateSidePanel: updateMerchantDetails,
  } = useSidePanel<MerchantType>(merchantList)

  const iconUrl = selectedDetailsMerchant?.wallet?.currency?.code
    ? [`${process.env.ICONS_URL}${selectedDetailsMerchant.wallet.currency.code.toLowerCase()}.svg`]
    : []

  const handleOpenEditMerchantSheet = (merchant: MerchantType) => {
    setAction({
      ...action,
      actionId: ActionIds.MERCHANT_EDIT,
      onClose: () => {
        setIsSheetOpen(false)
      },
      header: t('editMerchant'),
      meta: {
        merchant,
      },
    })
    setIsSheetOpen(true)
  }

  const handleOpenDetailsModal = useCallback(
    (merchantIndex: number) => {
      updateMerchantDetails(merchantIndex)
      setIsOpen(true)
    },
    [updateMerchantDetails, setIsOpen]
  )

  const onClose = useCallback(() => {
    setIsOpen(false)
  }, [setIsOpen])

  const merchantActionList = useMemo(() => {
    if (!merchantList) return []

    return merchantList.map((merchant: MerchantType, index: number) => {
      const wallet = merchant?.wallet

      return {
        key: merchant?.id,
        transparent: true,
        id: `wallet-row-${merchant?.id}`,
        title: merchant?.displayName,
        testid: `wallet-row-${merchant?.id}`,
        BodyPre: wallet?.description,
        BodyPost: wallet?.currency?.code,
        BodyAvatar: <Avatar name={wallet?.currency?.code} size="tiny" />,
        onItemClick: (e: { stopPropagation: () => void }) => {
          e?.stopPropagation()
          handleOpenDetailsModal(index)
        },
        RightPrimaryText: wallet?.balance
          ? `${numberWithCommas(wallet.balance)} ${wallet?.currency?.code}`
          : '-',
        CustomAction: (
          <DropdownMenu
            DropdownMenuItems={[
              canEdit ? (
                <>
                  <DropdownMenuItem
                    key={`${merchant?.wallet?.id}-details`}
                    LeadingIcon={<Icon name="Info" />}
                    onClick={() => handleOpenDetailsModal(index)}
                  >
                    {t('Details')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    key={`${merchant?.wallet?.id}-edit`}
                    LeadingIcon={<Icon name="SimplePencilCustom" size="small" />}
                    onClick={() => handleOpenEditMerchantSheet(merchant)}
                  >
                    {t('Edit')}
                  </DropdownMenuItem>
                </>
              ) : (
                <DropdownMenuItem
                  key={`${merchant?.wallet?.id}-details`}
                  LeadingIcon={<Icon name="Info" size="small" />}
                  onClick={() => handleOpenDetailsModal(index)}
                >
                  {t('Details')}
                </DropdownMenuItem>
              ),
            ]}
          >
            <Button iconElement={<Icon name="DotsThreeVerticalCustom" />} variant="ghost" />
          </DropdownMenu>
        ),
      }
    })
  }, [merchantList, canEdit, handleOpenDetailsModal, t])

  return (
    <Box className="overflow-y-hidden">
      <ActionList id="manage-merchants" items={merchantActionList} />

      <SidePanel
        id="side-panel-merchant-table"
        isOpen={isOpen}
        onClose={onClose}
        paging={pageMerchants}
        activeRowId={`wallet-row-${selectedDetailsMerchant?.id}`}
        onActiveRowChange={highlightRowBasedOnId}
        topOffset={bannerSpacing}
        header={
          <SidePanelHeaderTemplateTwo
            heading={{
              iconUrls: iconUrl,
              primaryLabel: `${selectedDetailsMerchant?.displayName}`,
              secondaryLabel: `${selectedDetailsMerchant?.wallet?.balance} ${selectedDetailsMerchant?.wallet?.currency?.code}`,
            }}
          />
        }
        body={<MerchantDetailsSummary merchant={selectedDetailsMerchant} />}
      />
      <MerchantSheet isSheetOpen={isSheetOpen} />
    </Box>
  )
}
